import classNames from 'classnames';
import type { ComponentPropsWithoutRef, ComponentPropsWithRef, ReactNode } from 'react';
import { Skeleton } from '../Skeleton';
import * as styles from './index.css';

export type SortDirection = 'asc' | 'desc';

function Container({ children }: { children: ReactNode }) {
    return <div className={styles.container}>{children}</div>;
}

function Root({ children, className, ...props }: ComponentPropsWithoutRef<'table'>) {
    return (
        <table className={classNames(styles.table, className)} {...props}>
            {children}
        </table>
    );
}

function Header({ children, className }: { children: ReactNode; className?: string }) {
    return (
        <thead className={classNames(styles.thead, className)}>
            <tr>{children}</tr>
        </thead>
    );
}

function Body({ children, ...props }: ComponentPropsWithoutRef<'tbody'>) {
    return <tbody {...props}>{children}</tbody>;
}

function Row({ children, className, ...props }: ComponentPropsWithRef<'tr'>) {
    return (
        <tr className={classNames(styles.tr, className)} {...props}>
            {children}
        </tr>
    );
}

function HeaderCell({ children, className, ...props }: ComponentPropsWithoutRef<'th'>) {
    return (
        <th className={classNames(styles.th, className)} {...props}>
            {children}
        </th>
    );
}

function Cell({ children, className, ...props }: ComponentPropsWithoutRef<'td'>) {
    return (
        <td className={classNames(styles.td, className)} {...props}>
            {children}
        </td>
    );
}

/** A single placeholder row. Callers decide how many to render. */
function RowSkeleton() {
    return (
        <Row style={{ height: '56px' }}>
            <Cell colSpan={9999}>
                <Skeleton.Line width="60%" height="sm" />
            </Cell>
        </Row>
    );
}

type SkeletonProps = {
    /** Number of header cells to lay out. */
    columns: number;
    /** Number of placeholder rows in the body. @default 3 */
    rows?: number;
};

/**
 * A full placeholder table — root, header, and body — for a `Table` whose data
 * hasn't loaded yet. Compose this into a larger skeleton rather than rebuilding
 * the table markup by hand.
 */
function TableSkeleton({ columns, rows = 3 }: SkeletonProps) {
    return (
        <Root>
            <Header>
                {Array.from({ length: columns }).map((_, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: loading placeholder, columns have no identity
                    <HeaderCell key={i}>
                        <Skeleton.Line width="60%" height="sm" />
                    </HeaderCell>
                ))}
            </Header>
            <Body>
                {Array.from({ length: rows }).map((_, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: loading placeholder, rows have no identity
                    <Row key={i}>
                        {Array.from({ length: columns }).map((_, j) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: loading placeholder, columns have no identity
                            <Cell key={j}>
                                <Skeleton.Line width="60%" height="sm" />
                            </Cell>
                        ))}
                    </Row>
                ))}
            </Body>
        </Root>
    );
}

function EmptyRow({ children }: { children: ReactNode }) {
    return (
        <Row>
            <Cell colSpan={9999} className={styles.emptyTd}>
                {children}
            </Cell>
        </Row>
    );
}

export interface SortButtonProps {
    children: ReactNode;
    onClick: () => void;
    direction?: SortDirection | null;
}

function SortButton({ children, onClick, direction = null }: SortButtonProps) {
    const arrow = direction === 'desc' ? '↓' : direction === 'asc' ? '↑' : null;
    return (
        <button type="button" className={styles.sortButton} onClick={onClick}>
            {children}
            {arrow && <span>{arrow}</span>}
        </button>
    );
}

type RowCountProps = {
    label: string;
    count: number;
    unit?: string;
};

function RowCount({ label, count, unit = '' }: RowCountProps) {
    return (
        <p className={styles.rowCount}>
            {label}：{count}
            {unit}
        </p>
    );
}

export const Table = {
    Container,
    Root,
    Header,
    Body,
    Row,
    HeaderCell,
    Cell,
    SortButton,
    RowSkeleton,
    Skeleton: TableSkeleton,
    EmptyRow,
    RowCount,
};
